'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimeSlot {
  time: string;
  available: boolean;
}

interface CalendarDay {
  date: number;
  month: number;
  year: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isPast: boolean;
  slots: TimeSlot[];
}

interface CalendarViewProps {
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
  selectedTime: string | null;
  onTimeSelect: (time: string) => void;
}

export default function CalendarView({
  selectedDate,
  onDateSelect,
  selectedTime,
  onTimeSelect,
}: CalendarViewProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date(2025, 0, 1));
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;
    generateCalendarDays();
  }, [currentMonth, isHydrated]);

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const today = new Date(2025, 0, 22);

    const days: CalendarDay[] = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      const prevMonthDay = new Date(year, month, -startingDayOfWeek + i + 1);
      days.push({
        date: prevMonthDay.getDate(),
        month: prevMonthDay.getMonth(),
        year: prevMonthDay.getFullYear(),
        isCurrentMonth: false,
        isToday: false,
        isPast: true,
        slots: [],
      });
    }

    for (let date = 1; date <= lastDay.getDate(); date++) {
      const currentDate = new Date(year, month, date);
      const isPast = currentDate < today;
      const isToday =
        currentDate.getDate() === today.getDate() &&
        currentDate.getMonth() === today.getMonth() &&
        currentDate.getFullYear() === today.getFullYear();

      days.push({
        date,
        month,
        year,
        isCurrentMonth: true,
        isToday,
        isPast,
        slots: generateTimeSlots(date),
      });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextMonthDay = new Date(year, month + 1, i);
      days.push({
        date: nextMonthDay.getDate(),
        month: nextMonthDay.getMonth(),
        year: nextMonthDay.getFullYear(),
        isCurrentMonth: false,
        isToday: false,
        isPast: true,
        slots: [],
      });
    }

    setCalendarDays(days);
  };

  const generateTimeSlots = (date: number): TimeSlot[] => {
    const slots: TimeSlot[] = [
      { time: '10:00 AM', available: date % 3 !== 0 },
      { time: '11:00 AM', available: date % 2 !== 0 },
      { time: '12:00 PM', available: date % 4 !== 0 },
      { time: '2:00 PM', available: date % 3 !== 0 },
      { time: '3:00 PM', available: date % 5 !== 0 },
      { time: '4:00 PM', available: date % 2 !== 0 },
    ];
    return slots;
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (day: CalendarDay) => {
    if (day.isPast || !day.isCurrentMonth) return;
    const newDate = new Date(day.year, day.month, day.date);
    onDateSelect(newDate);
  };

  const isDateSelected = (day: CalendarDay): boolean => {
    if (!selectedDate || !day.isCurrentMonth) return false;
    return (
      selectedDate.getDate() === day.date &&
      selectedDate.getMonth() === day.month &&
      selectedDate.getFullYear() === day.year
    );
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:160:6" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="160" data-component-end-line="178" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20p-8%22%7D" className="bg-card border border-border p-8">
        <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:161:8" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="161" data-component-end-line="167" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-8%22%7D" className="flex items-center justify-between mb-8">
          <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:162:10" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="162" data-component-end-line="162" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20w-32%20bg-muted%22%7D" className="h-8 w-32 bg-muted" />
          <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:163:10" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="163" data-component-end-line="166" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20space-x-2%22%7D" className="flex space-x-2">
            <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:164:12" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="164" data-component-end-line="164" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-10%20h-10%20bg-muted%22%7D" className="w-10 h-10 bg-muted" />
            <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:165:12" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="165" data-component-end-line="165" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-10%20h-10%20bg-muted%22%7D" className="w-10 h-10 bg-muted" />
          </div>
        </div>
        <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:168:8" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="168" data-component-end-line="172" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-7%20gap-2%20mb-4%22%7D" className="grid grid-cols-7 gap-2 mb-4">
          {weekDays.map((day) => (
            <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:170:12" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="170" data-component-end-line="170" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%22%7D" key={day} className="h-8 bg-muted" />
          ))}
        </div>
        <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:173:8" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="173" data-component-end-line="177" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-7%20gap-2%22%7D" className="grid grid-cols-7 gap-2">
          {Array.from({ length: 35 }).map((_, i) => (
            <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:175:12" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="175" data-component-end-line="175" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%22%7D" key={i} className="h-12 bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  const selectedDay = calendarDays.find((day) => isDateSelected(day));

  return (
    <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:185:4" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="185" data-component-end-line="266" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
      <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:186:6" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="186" data-component-end-line="241" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20p-8%22%7D" className="bg-card border border-border p-8">
        <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:187:8" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="187" data-component-end-line="207" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-8%22%7D" className="flex items-center justify-between mb-8">
          <h3 data-component-id="src/app/appointment-booking/components/CalendarView.tsx:188:10" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="188" data-component-end-line="190" data-component-file="CalendarView.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-display%20text-2xl%20text-foreground%22%7D" className="font-display text-2xl text-foreground">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:191:10" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="191" data-component-end-line="206" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20space-x-2%22%7D" className="flex space-x-2">
            <button data-component-id="src/app/appointment-booking/components/CalendarView.tsx:192:12" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="192" data-component-end-line="198" data-component-file="CalendarView.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-10%20h-10%20border%20border-border%20hover%3Aborder-accent%20hover%3Abg-muted%20transition-all%20duration-300%20flex%20items-center%20justify-center%22%7D"
              onClick={handlePreviousMonth}
              className="w-10 h-10 border border-border hover:border-accent hover:bg-muted transition-all duration-300 flex items-center justify-center"
              aria-label="Previous month"
            >
              <Icon data-component-id="src/app/appointment-booking/components/CalendarView.tsx:197:14" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="197" data-component-end-line="197" data-component-file="CalendarView.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronLeftIcon%22%7D" name="ChevronLeftIcon" size={20} />
            </button>
            <button data-component-id="src/app/appointment-booking/components/CalendarView.tsx:199:12" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="199" data-component-end-line="205" data-component-file="CalendarView.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-10%20h-10%20border%20border-border%20hover%3Aborder-accent%20hover%3Abg-muted%20transition-all%20duration-300%20flex%20items-center%20justify-center%22%7D"
              onClick={handleNextMonth}
              className="w-10 h-10 border border-border hover:border-accent hover:bg-muted transition-all duration-300 flex items-center justify-center"
              aria-label="Next month"
            >
              <Icon data-component-id="src/app/appointment-booking/components/CalendarView.tsx:204:14" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="204" data-component-end-line="204" data-component-file="CalendarView.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronRightIcon%22%7D" name="ChevronRightIcon" size={20} />
            </button>
          </div>
        </div>

        <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:209:8" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="209" data-component-end-line="218" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-7%20gap-2%20mb-4%22%7D" className="grid grid-cols-7 gap-2 mb-4">
          {weekDays.map((day) => (
            <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:211:12" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="211" data-component-end-line="216" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-body%20text-xs%20text-muted-foreground%20text-center%20py-2%20tracking-wide%22%7D"
              key={day}
              className="font-body text-xs text-muted-foreground text-center py-2 tracking-wide"
            >
              {day}
            </div>
          ))}
        </div>

        <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:220:8" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="220" data-component-end-line="240" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-7%20gap-2%22%7D" className="grid grid-cols-7 gap-2">
          {calendarDays.map((day, index) => (
            <button data-component-id="src/app/appointment-booking/components/CalendarView.tsx:222:12" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="222" data-component-end-line="238" data-component-file="CalendarView.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={index}
              onClick={() => handleDateClick(day)}
              disabled={day.isPast || !day.isCurrentMonth}
              className={`h-12 font-body text-sm transition-all duration-300 ${
                !day.isCurrentMonth
                  ? 'text-muted-foreground opacity-30 cursor-default'
                  : day.isPast
                  ? 'text-muted-foreground opacity-50 cursor-not-allowed'
                  : isDateSelected(day)
                  ? 'bg-accent text-accent-foreground font-medium'
                  : day.isToday
                  ? 'border border-accent text-accent font-medium hover:bg-muted' :'text-foreground hover:bg-muted hover:border hover:border-border'
              }`}
            >
              {day.date}
            </button>
          ))}
        </div>
      </div>

      {selectedDate && selectedDay && (
        <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:244:8" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="244" data-component-end-line="264" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20p-8%22%7D" className="bg-card border border-border p-8">
          <h3 data-component-id="src/app/appointment-booking/components/CalendarView.tsx:245:10" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="245" data-component-end-line="245" data-component-file="CalendarView.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-display%20text-xl%20text-foreground%20mb-6%22%2C%22textContent%22%3A%22Available%20Time%20Slots%22%7D" className="font-display text-xl text-foreground mb-6">Available Time Slots</h3>
          <div data-component-id="src/app/appointment-booking/components/CalendarView.tsx:246:10" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="246" data-component-end-line="263" data-component-file="CalendarView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20sm%3Agrid-cols-3%20gap-3%22%7D" className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {selectedDay.slots.map((slot) => (
              <button data-component-id="src/app/appointment-booking/components/CalendarView.tsx:248:14" data-component-path="src/app/appointment-booking/components/CalendarView.tsx" data-component-line="248" data-component-end-line="261" data-component-file="CalendarView.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={slot.time}
                onClick={() => slot.available && onTimeSelect(slot.time)}
                disabled={!slot.available}
                className={`py-3 px-4 font-body text-sm transition-all duration-300 ${
                  selectedTime === slot.time
                    ? 'bg-accent text-accent-foreground'
                    : slot.available
                    ? 'border border-border hover:border-accent hover:bg-muted text-foreground'
                    : 'border border-border text-muted-foreground opacity-50 cursor-not-allowed'
                }`}
              >
                {slot.time}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}